/**
 * Form ↔ model conversion and validation for the SLA admin page (policies,
 * business calendars, routing rules). Pure; errors are i18n keys.
 */
import type { BusinessCalendarInput, RoutingRuleInput, SlaPolicyInput, WeekHours } from '@/api/adminApi'

export const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const
export const PAUSABLE_STATUSES = ['waiting_on_customer', 'waiting_on_vendor', 'scheduled', 'escalated_engineering'] as const

export type Errors<K extends string> = Partial<Record<K, string>>

// ─── SLA policy ──────────────────────────────────────────────────────────────

export interface SlaPolicyForm {
  name: string
  priority: string
  firstResponseMins: string
  resolutionMins: string
  calendarId: string
  pauseStatuses: string[]
  warnAtPct: string
  customerId: string
  productId: string
  active: boolean
}

export const EMPTY_POLICY_FORM: SlaPolicyForm = {
  name: '',
  priority: 'P3',
  firstResponseMins: '',
  resolutionMins: '',
  calendarId: '',
  pauseStatuses: ['waiting_on_customer', 'scheduled'],
  warnAtPct: '75',
  customerId: '',
  productId: '',
  active: true,
}

export function policyToForm(p: SlaPolicyInput): SlaPolicyForm {
  return {
    name: p.name,
    priority: p.priority,
    firstResponseMins: String(p.firstResponseMins),
    resolutionMins: String(p.resolutionMins),
    calendarId: p.calendarId,
    pauseStatuses: [...p.pauseStatuses],
    warnAtPct: String(p.warnAtPct),
    customerId: p.customerId ?? '',
    productId: p.productId ?? '',
    active: p.active,
  }
}

function positiveInt(v: string): number | null {
  if (!/^\s*\d+\s*$/.test(v)) return null
  const n = Number(v)
  return n > 0 ? n : null
}

export function validatePolicy(f: SlaPolicyForm): Errors<keyof SlaPolicyForm> {
  const e: Errors<keyof SlaPolicyForm> = {}
  if (!f.name.trim()) e.name = 'admin.errors.required'
  if (!['P1', 'P2', 'P3', 'P4'].includes(f.priority)) e.priority = 'admin.errors.required'
  const first = positiveInt(f.firstResponseMins)
  const resolution = positiveInt(f.resolutionMins)
  if (first === null) e.firstResponseMins = 'admin.errors.positiveMinutes'
  if (resolution === null) e.resolutionMins = 'admin.errors.positiveMinutes'
  if (first !== null && resolution !== null && first > resolution) e.firstResponseMins = 'admin.errors.firstAfterResolution'
  if (!f.calendarId) e.calendarId = 'admin.errors.required'
  const warn = positiveInt(f.warnAtPct)
  if (warn === null || warn >= 100) e.warnAtPct = 'admin.errors.percent'
  return e
}

export function formToPolicy(f: SlaPolicyForm): SlaPolicyInput {
  return {
    name: f.name.trim(),
    priority: f.priority,
    firstResponseMins: Number(f.firstResponseMins),
    resolutionMins: Number(f.resolutionMins),
    calendarId: f.calendarId,
    pauseStatuses: f.pauseStatuses,
    warnAtPct: Number(f.warnAtPct),
    customerId: f.customerId || null,
    productId: f.productId || null,
    active: f.active,
  }
}

/** `240` → `4h`, `90` → `1h 30m`, `1440` → `1d`, `2880` → `2d`. */
export function formatMinutes(mins: number): string {
  if (mins % 1440 === 0) return `${mins / 1440}d`
  if (mins < 60) return `${mins}m`
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return m ? `${h}h ${m}m` : `${h}h`
}

// ─── Business calendar ───────────────────────────────────────────────────────

export interface CalendarForm {
  name: string
  timeZone: string
  is24x7: boolean
  /** Per weekday, `09:00-17:00` ranges separated by commas; empty = closed. */
  hours: Record<string, string>
  /** One `YYYY-MM-DD` per line or comma. */
  holidays: string
}

export const EMPTY_CALENDAR_FORM: CalendarForm = {
  name: '',
  timeZone: 'customer',
  is24x7: false,
  hours: { mon: '09:00-17:00', tue: '09:00-17:00', wed: '09:00-17:00', thu: '09:00-17:00', fri: '09:00-17:00', sat: '', sun: '' },
  holidays: '',
}

const RANGE = /^([01]\d|2[0-3]):([0-5]\d)\s*-\s*([01]\d|2[0-4]):([0-5]\d)$/

/** `09:00-12:00, 13:00-17:00` → `[["09:00","12:00"],["13:00","17:00"]]`, or null when malformed. */
export function parseDayHours(text: string): [string, string][] | null {
  const trimmed = text.trim()
  if (!trimmed) return []
  const out: [string, string][] = []
  for (const part of trimmed.split(',')) {
    const m = RANGE.exec(part.trim())
    if (!m) return null
    const start = `${m[1]}:${m[2]}`
    const end = `${m[3]}:${m[4]}`
    if (end <= start || (m[3] === '24' && m[4] !== '00')) return null
    out.push([start, end])
  }
  return out
}

export function formatDayHours(ranges: [string, string][] | undefined): string {
  return (ranges ?? []).map(([a, b]) => `${a}-${b}`).join(', ')
}

export function parseHolidays(text: string): string[] | null {
  const items = text
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean)
  for (const d of items) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d) || Number.isNaN(Date.parse(`${d}T00:00:00Z`))) return null
  }
  return Array.from(new Set(items)).sort()
}

export function isValidTimeZone(tz: string): boolean {
  if (tz === 'customer') return true
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz })
    return true
  } catch {
    return false
  }
}

export function calendarToForm(c: BusinessCalendarInput): CalendarForm {
  const hours: Record<string, string> = {}
  for (const d of WEEKDAYS) hours[d] = formatDayHours(c.hours?.[d])
  return { name: c.name, timeZone: c.timeZone, is24x7: c.is24x7, hours, holidays: (c.holidays ?? []).join('\n') }
}

export function validateCalendar(f: CalendarForm): Errors<string> {
  const e: Errors<string> = {}
  if (!f.name.trim()) e.name = 'admin.errors.required'
  if (!f.timeZone.trim() || !isValidTimeZone(f.timeZone.trim())) e.timeZone = 'admin.errors.timeZone'
  if (!f.is24x7) {
    let open = false
    for (const d of WEEKDAYS) {
      const r = parseDayHours(f.hours[d] ?? '')
      if (r === null) e[`hours.${d}`] = 'admin.errors.hours'
      else if (r.length > 0) open = true
    }
    if (!open && !Object.keys(e).some((k) => k.startsWith('hours.'))) e.hours = 'admin.errors.noHours'
  }
  if (parseHolidays(f.holidays) === null) e.holidays = 'admin.errors.holidays'
  return e
}

export function formToCalendar(f: CalendarForm): BusinessCalendarInput {
  const hours: WeekHours = {}
  for (const d of WEEKDAYS) hours[d] = f.is24x7 ? [['00:00', '24:00']] : (parseDayHours(f.hours[d] ?? '') ?? [])
  return { name: f.name.trim(), timeZone: f.timeZone.trim(), is24x7: f.is24x7, hours, holidays: parseHolidays(f.holidays) ?? [] }
}

// ─── Routing rule ────────────────────────────────────────────────────────────

export interface RoutingRuleForm {
  name: string
  order: string
  active: boolean
  conditions: string
  actions: string
}

export const EMPTY_RULE_FORM: RoutingRuleForm = {
  name: '',
  order: '10',
  active: true,
  conditions: '{\n  "products": ["cgt"],\n  "priorities": ["P1"]\n}',
  actions: '{\n  "priority": "P1"\n}',
}

export function ruleToForm(r: RoutingRuleInput): RoutingRuleForm {
  return {
    name: r.name,
    order: String(r.order),
    active: r.active,
    conditions: JSON.stringify(r.conditions ?? {}, null, 2),
    actions: JSON.stringify(r.actions ?? {}, null, 2),
  }
}

function parseObject(text: string): Record<string, unknown> | unknown[] | null {
  try {
    const v: unknown = JSON.parse(text)
    return v !== null && typeof v === 'object' ? (v as Record<string, unknown> | unknown[]) : null
  } catch {
    return null
  }
}

export function validateRule(f: RoutingRuleForm): Errors<keyof RoutingRuleForm> {
  const e: Errors<keyof RoutingRuleForm> = {}
  if (!f.name.trim()) e.name = 'admin.errors.required'
  if (!/^\s*\d+\s*$/.test(f.order)) e.order = 'admin.errors.order'
  if (parseObject(f.conditions) === null) e.conditions = 'admin.errors.json'
  if (parseObject(f.actions) === null) e.actions = 'admin.errors.json'
  return e
}

export function formToRule(f: RoutingRuleForm): RoutingRuleInput {
  return {
    name: f.name.trim(),
    order: Number(f.order),
    active: f.active,
    conditions: parseObject(f.conditions) ?? {},
    actions: parseObject(f.actions) ?? {},
  }
}
